import React, { useState, useRef, useEffect } from 'react';
import { ArrowButtonLeft, ArrowButtonRight, FullscreenButton, Img } from './OpenSeaDragon.style';
import { IOpenSeaDragon } from './OpenSeaDragon.i';
import { useScrollControl } from '../AriaControls/BaseComponents/Modals/useScrollControl';

let openSeadragon: any = undefined;

export const OpenSeaDragon = ({ photo, className, dzi=false, bg=true, fullscreen=false, navNext, navPrev, onEsc, showNavOutsideFullscreen, children }:
IOpenSeaDragon) => {
  const image = useRef();
  const osd = useRef();
  const [viewer, setViewer] = useState<any>(undefined);

  const [isFullscreen, setIsFullscreen] = useState(false);

  useScrollControl(isFullscreen);

  const createViewer = (img: string, tile: string | false = false) => {
    if(viewer) viewer.destroy();
    // use dzi as source if provided. otherwise load standard img
    const tileSources = tile ? tile : {
      type: 'image',
      url: img,
    };
    const newViewer = openSeadragon({
      element: osd.current,
      tileSources,
      prefixUrl: 'https://openseadragon.github.io/openseadragon/images/',
      ajaxWithCredentials: false,
      visibilityRatio: 1,
      maxZoomPixelRatio: 1.5,
      showFullScreenControl: true,
      showFullPageControl: false,
      constrainDuringPan: true,
    });
    newViewer.addHandler('open-failed', ({ eventSource, message, source }) => {
      // source is a string if trying to load a dzi. object w/ url value if loading standard image

      // NotFound image failed to load. Remove osd viewer to avoid infinite loop
      if(source.url === `${window.CDN_PATH}notfound_full.png`) {
        newViewer.destroy();
      }
      // Use standard photo passed in if dzi is not available
      if(source === tile) {
        newViewer.open({
          url: img,
          type: 'image',
        });
      }
      // Standard Photo has already failed to open. Load NotFound image
      if(img === 'notfound' || source.url === img) {
        newViewer.open({
          url: `${window.CDN_PATH}notfound_full.png`,
          type: 'image',
        });
      }
    });
    image.current = img;
    setViewer(newViewer);
  };

  useEffect(() => {
    (async () => {
      if(!openSeadragon) {
        await import('openseadragon').then((x) => openSeadragon = x.default);
      }
      if(document && viewer && !photo) viewer.destroy();
      if(document && image.current !== photo) createViewer(photo, dzi);
    })();
  }, [photo, dzi]);

  function onKeyDown(e) {
    switch (e?.keyCode) {
      case 27:
      case 13: // We bypassed the focus for the Close Fullscreen button, so capture enter here preserve close behavior
        onEsc ? onEsc() : setIsFullscreen(false);
        break;
      case 39:
        navNext && navNext();
        break;
      case 37:
        navPrev && navPrev();
        break;
    }
  }

  useEffect(() => {
    if(!viewer || !isFullscreen) {
      document.removeEventListener('keydown', onKeyDown);
      return;
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [viewer, isFullscreen]);

  return (
    <Img bg={bg} fullscreen={isFullscreen} className={className}>
      {((showNavOutsideFullscreen || isFullscreen) && navPrev)
        && <ArrowButtonLeft onPress={navPrev} aria-label='Previous Photo'/>
      }
      {((showNavOutsideFullscreen || isFullscreen) && navNext)
        && <ArrowButtonRight onPress={navNext} aria-label='Next Photo'/>
      }
      {!fullscreen
        && <FullscreenButton onPress={() => setIsFullscreen(!isFullscreen)} blurOnClick>
          {isFullscreen && 'Close' || 'Open'} Fullscreen
        </FullscreenButton>
      }
      {children}
      <div id="osd" ref={osd} />
    </Img>
  );
};
